import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Home, Key, Building2, Briefcase, Shield, ArrowRight, UserCheck } from 'lucide-react';

const ROLE_INFO = {
  buyer: {
    label: 'Buyer',
    icon: <Home className="h-5 w-5" />,
    color: '#E07A3A',
    bgColor: '#FEF3EC',
    dashboard: '/buyer',
    description: 'Browse properties, book viewings, and connect with verified sellers.',
  },
  tenant: {
    label: 'Tenant',
    icon: <Key className="h-5 w-5" />,
    color: '#22C55E',
    bgColor: '#F0FDF4',
    dashboard: '/buyer',
    description: 'Search rental properties and connect with verified landlords.',
  },
  seller: {
    label: 'Seller',
    icon: <Building2 className="h-5 w-5" />,
    color: '#3B82F6',
    bgColor: '#EFF6FF',
    dashboard: '/seller',
    description: 'List properties, manage listings, and connect with verified buyers.',
  },
  agent: {
    label: 'Agent',
    icon: <UserCheck className="h-5 w-5" />,
    color: '#0EA5E9',
    bgColor: '#F0F9FF',
    dashboard: '/agent',
    description: 'Browse properties, manage deals, and connect buyers with sellers.',
  },
  professional: {
    label: 'Professional',
    icon: <Briefcase className="h-5 w-5" />,
    color: '#8B5CF6',
    bgColor: '#F5F3FF',
    dashboard: '/professional',
    description: 'Manage client leads, access compliance data, and represent clients.',
  },
  admin: {
    label: 'Admin',
    icon: <Shield className="h-5 w-5" />,
    color: '#EF4444',
    bgColor: '#FEF2F2',
    dashboard: '/admin',
    description: 'Platform administration, user management, and settings.',
  },
};

const SECTION_ROLES = {
  '/buyer': ['buyer', 'tenant'],
  '/seller': ['seller'],
  '/agent': ['agent'],
  '/professional': ['professional'],
  '/admin': ['admin'],
};

export const RoleGuideBanner = ({ currentSection }) => {
  const { user } = useAuth();
  if (!user) return null;

  const userInfo = ROLE_INFO[user.role] || ROLE_INFO.buyer;
  const allowedRoles = SECTION_ROLES[currentSection] || [];
  const isWrongSection = allowedRoles.length > 0 && !allowedRoles.includes(user.role) && user.role !== 'admin';

  if (isWrongSection) {
    return (
      <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6" data-testid="wrong-section-banner">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm font-semibold">
              You're signed in as a <span className="uppercase">{userInfo.label}</span> — this section is for {allowedRoles.map(r => ROLE_INFO[r]?.label).join('/')}s.
            </span>
          </div>
          <Link
            to={userInfo.dashboard}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold tracking-wider uppercase transition-colors"
            style={{ backgroundColor: userInfo.color, color: 'white' }}
            data-testid="go-to-dashboard-btn"
          >
            Go to {userInfo.label} Dashboard <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    );
  }

  // Show a compact role indicator on the correct dashboard
  return (
    <div
      className="px-4 py-2 mb-2 flex items-center gap-2 text-xs tracking-wider uppercase font-semibold"
      style={{ backgroundColor: userInfo.bgColor, color: userInfo.color }}
      data-testid="role-indicator"
    >
      {userInfo.icon}
      <span>{userInfo.label}: {user.name}</span>
      <span className="hidden sm:inline opacity-70 normal-case tracking-normal ml-2">{userInfo.description}</span>
    </div>
  );
};
